'use client';

import { useState } from 'react';
import axiosInstance from '@libs/axios';
import { notifications } from '@mantine/notifications';
import { apiUrl } from '@constants/url';

export type LogoFeedbackKind = 'WRONG_LOGO' | 'SUGGEST_WEBSITE' | 'MISSING_LOGO';

interface ReportPayload {
  companyName: string;
  kind: LogoFeedbackKind;
  suggestedWebsite?: string;
  observedLogoUrl?: string | null;
  observedWebsite?: string | null;
}

/**
 * Reports a wrong or missing company logo.
 *
 * The company NAME is sent rather than an id: the table renders names and has
 * no foreign key to the enrichment data, and the backend already owns the
 * normalise-and-hash that turns one into the other.
 *
 * Nothing in the table is refreshed afterwards. A report goes to a review
 * queue, so the logo does not change now — pretending otherwise by optimistically
 * clearing it would be a lie about what just happened.
 */
export default function useReportCompanyLogo() {
  const [loading, setLoading] = useState(false);

  const report = async (payload: ReportPayload): Promise<boolean> => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.post<{ data?: { message?: string } }>(
        apiUrl.companyEnrichment.feedback,
        payload
      );
      notifications.show({
        title: 'Terima kasih',
        message: data?.data?.message || 'Laporan Anda sudah kami terima.',
        color: 'green',
      });
      return true;
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      notifications.show({
        title: 'Gagal mengirim laporan',
        message: detail || 'Laporan tidak terkirim. Silakan coba lagi.',
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { report, loading };
}
